'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { InstrumentRow } from '@/components/catalogo/types';

type InstrumentCacheState = {
  tickers: InstrumentRow[];
  loading: boolean;
  error: string | null;
};

const TICKER_CATALOG_CACHE_VERSION = '2';
const InstrumentCacheContext = createContext<InstrumentCacheState | null>(null);

export const InstrumentCacheProvider = ({ children }: { children: React.ReactNode }) => {
  const [tickers, setInstruments] = useState<InstrumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const loadInstruments = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/catalogo/tickers?v=${TICKER_CATALOG_CACHE_VERSION}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          throw new Error(`Erro ao carregar tickers (${res.status})`);
        }
        const data = (await res.json()) as InstrumentRow[];
        if (!cancelled) {
          setInstruments(data);
        }
      } catch (err) {
        if (!cancelled && (err as { name?: string }).name !== 'AbortError') {
          setError('Falha ao carregar tickers');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadInstruments();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const value = useMemo(() => ({ tickers, loading, error }), [tickers, loading, error]);

  return <InstrumentCacheContext.Provider value={value}>{children}</InstrumentCacheContext.Provider>;
};

export const useInstrumentCache = () => {
  const context = useContext(InstrumentCacheContext);
  if (!context) {
    throw new Error('useInstrumentCache must be used within InstrumentCacheProvider');
  }
  return context;
};

export const TickerCacheProvider = InstrumentCacheProvider;
export const useTickerCache = useInstrumentCache;
